// src/Admin/DemoTeacherAvailability.js
// Demo student profile → "Teacher times" tab.
// The weekly paid-class times of the teacher who gave the demo, so the admin
// can answer parents who ask "which times are available?" before enrolling.
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarDays, faCopy, faCheck, faChalkboardUser, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { formatInZone, cityOf } from './Studentzone';
import './DemoTeacherAvailability.css';

const API = process.env.REACT_APP_API_BASE_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('userData')}` } });

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

// "16:30" -> "4:30 PM"
const twelve = (hhmm) => {
  const [h, m] = hhmm.split(':').map(Number);
  const hour = h % 12 || 12;
  return `${hour}:${String(m).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`;
};

const DemoTeacherAvailability = ({ studentId, tzName, tzHours, hasZone }) => {
  const [state, setState] = useState({ loading: true, error: '', teacher: null, slots: [] });
  const [showBooked, setShowBooked] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/api/admin/studentprofile/demoteacher/${studentId}`, auth());
        if (cancelled) return;
        setState({ loading: false, error: '', teacher: res.data?.teacher || null, slots: Array.isArray(res.data?.slots) ? res.data.slots : [] });
      } catch (err) {
        if (!cancelled) setState({ loading: false, error: err.response?.data?.error || 'We couldn’t load the teacher’s times.', teacher: null, slots: [] });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [studentId]);

  // parent's time only when it's a different zone than Lebanon
  const theirTime = (utc) => (hasZone && tzName !== 'Asia/Beirut' ? formatInZone(utc, tzName, tzHours, { weekday: 'short', hour: 'numeric', minute: '2-digit' }) : '');
  const place = cityOf(tzName) || 'their time';

  const byDay = useMemo(() => {
    const map = DAYS.map((label, i) => ({ day: i + 1, label, free: [], booked: [] }));
    state.slots.forEach((s) => {
      const row = map[s.day - 1];
      if (row) (s.booked ? row.booked : row.free).push(s);
    });
    return map;
  }, [state.slots]);

  const freeCount = state.slots.filter((s) => !s.booked).length;

  const copyText = () => {
    const lines = byDay
      .filter((d) => d.free.length)
      .map((d) => {
        const times = d.free
          .map((s) => {
            const local = theirTime(s.utc);
            return local ? `${twelve(s.lebanonTime)} (${local} ${place})` : twelve(s.lebanonTime);
          })
          .join(', ');
        return `${d.label}: ${times}`;
      });
    const text = `Available times for weekly classes (Lebanon time):\n${lines.join('\n')}`;
    navigator.clipboard?.writeText(text).then(
      () => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1800);
      },
      () => {}
    );
  };

  if (state.loading) return <div className="rmdta-skel" />;
  if (state.error) {
    return (
      <div className="rmasp-empty">
        <FontAwesomeIcon icon={faTriangleExclamation} className="is-error" />
        <p>{state.error}</p>
      </div>
    );
  }
  if (!state.teacher) {
    return (
      <div className="rmasp-empty">
        <FontAwesomeIcon icon={faChalkboardUser} />
        <p>No teacher is assigned to this demo class yet.</p>
      </div>
    );
  }

  const name = [state.teacher.firstName, state.teacher.lastName].filter(Boolean).join(' ') || 'Teacher';

  return (
    <section className="rmdta">
      <div className="rmasp-sectionhead">
        <div>
          <h2 className="rmasp-h2">
            <FontAwesomeIcon icon={faCalendarDays} /> Teacher times
          </h2>
          <p>
            Weekly class times of <Link to={`/Admin/teacherprofile/${state.teacher.id}`}>{name}</Link> · <b>{freeCount}</b> free
            {hasZone && tzName !== 'Asia/Beirut' && <span className="rmasp-dim"> · parent’s time shown for {place}</span>}
          </p>
        </div>
        <div className="rmdta-actions">
          <label className="rmdta-switch">
            <input type="checkbox" checked={showBooked} onChange={(e) => setShowBooked(e.target.checked)} />
            Show booked
          </label>
          <button type="button" className="rmasp-btn" onClick={copyText} disabled={!freeCount}>
            <FontAwesomeIcon icon={copied ? faCheck : faCopy} /> {copied ? 'Copied' : 'Copy free times'}
          </button>
        </div>
      </div>

      {state.slots.length === 0 ? (
        <div className="rmasp-empty">
          <FontAwesomeIcon icon={faCalendarDays} />
          <p>{name} hasn’t set any weekly availability yet.</p>
        </div>
      ) : (
        <ul className="rmdta-days">
          {byDay.map((d) => {
            const list = showBooked ? [...d.free, ...d.booked].sort((a, b) => a.slot - b.slot) : d.free;
            return (
              <li key={d.day} className="rmdta-day">
                <div className="rmdta-dayname">
                  <strong>{d.label}</strong>
                  <span>{d.free.length ? `${d.free.length} free` : 'No free time'}</span>
                </div>
                <div className="rmdta-times">
                  {list.length === 0 ? (
                    <span className="rmdta-none">—</span>
                  ) : (
                    list.map((s) => {
                      const local = theirTime(s.utc);
                      return (
                        <span key={s.slot} className={`rmdta-time ${s.booked ? 'is-booked' : 'is-free'}`}>
                          <strong>{twelve(s.lebanonTime)}</strong>
                          {s.booked ? <small>Booked</small> : local && <small>{local}</small>}
                        </span>
                      );
                    })
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
      <p className="rmdta-note">Times are in Lebanon time. “Booked” means an enrolled student already has this weekly slot.</p>
    </section>
  );
};

export default DemoTeacherAvailability;
